import React, { useEffect, useMemo } from 'react';
import { useAnimation } from '../hooks/useAnimation';
import BigBtn from '../design/components/BigBtn';
import ThemePicker from '../design/components/ThemePicker';
import { playerStyleFor } from '../design/themes';
import { useIsDesktop } from '../hooks/useIsDesktop';

const PODIUM_HEIGHTS_MOBILE = [110, 84, 60];
const PODIUM_HEIGHTS_DESKTOP = [180, 130, 95];
const PODIUM_TINTS = ['var(--c-yellow)', 'var(--c-pink)', 'var(--c-blue)'];
const PODIUM_LABELS = ['1ST', '2ND', '3RD'];

const formatDistance = (d) => (typeof d === 'number' ? `${d.toFixed(1)} OFF` : '—');

const indexOfPlayer = (players, id) => {
  return players.findIndex((p) => (typeof p === 'string' ? p : p?.id || p?.name) === id);
};

const GuessGameFinishedScreen = ({
  finalScores = [],
  roundHistory = [],
  totalRounds = 5,
  players = [],
  currentPlayerId = null,
  onPlayAgain,
  onExitToHome,
}) => {
  const { createConfetti } = useAnimation();

  const sortedScores = useMemo(() => {
    return [...finalScores].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  }, [finalScores]);

  const winner = sortedScores[0];
  const topThree = sortedScores.slice(0, 3);
  const rest = sortedScores.slice(3);

  // Display order on the podium board: 2nd · 1st · 3rd. The `rank` is the
  // 0-indexed position into PODIUM_HEIGHTS so the 1st-place block sits taller.
  const podiumOrder = [
    topThree[1] && { entry: topThree[1], rank: 1 },
    topThree[0] && { entry: topThree[0], rank: 0 },
    topThree[2] && { entry: topThree[2], rank: 2 },
  ].filter(Boolean);

  const myRoundScores = useMemo(() => {
    return roundHistory.map((rh) => {
      const mine = (rh.results || []).find((r) => r.playerId === currentPlayerId);
      return {
        round: rh.round,
        score: mine?.score ?? 0,
        distance: mine?.distance ?? null,
        cricketer: rh.cricketer?.name || null,
      };
    });
  }, [roundHistory, currentPlayerId]);

  const closest = useMemo(() => {
    const withDist = myRoundScores.filter((r) => typeof r.distance === 'number');
    if (!withDist.length) return null;
    return withDist.reduce((best, r) => (best == null || r.distance < best.distance ? r : best), null);
  }, [myRoundScores]);

  const furthest = useMemo(() => {
    const withDist = myRoundScores.filter((r) => typeof r.distance === 'number');
    if (!withDist.length) return null;
    return withDist.reduce((worst, r) => (worst == null || r.distance > worst.distance ? r : worst), null);
  }, [myRoundScores]);

  const myEntry = sortedScores.find((s) => s.id === currentPlayerId);
  const myRank = myEntry ? sortedScores.indexOf(myEntry) + 1 : null;
  const isWinner = winner?.id === currentPlayerId;

  useEffect(() => {
    createConfetti();
    const t1 = setTimeout(() => createConfetti(), 1000);
    const t2 = setTimeout(() => createConfetti(), 2000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [createConfetti]);

  const isDesktop = useIsDesktop();
  const PODIUM_HEIGHTS = isDesktop ? PODIUM_HEIGHTS_DESKTOP : PODIUM_HEIGHTS_MOBILE;

  return (
    <div className="pen-screen" style={{ paddingBottom: 24 }}>
      {/* Top strip */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 16px 6px', gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'var(--c-yellow)',
            border: 'var(--border-ink)',
            boxShadow: 'var(--shadow-hard-sm)',
            display: 'grid', placeItems: 'center',
            fontSize: 18,
          }}>★</div>
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
            letterSpacing: 0.8, color: 'var(--c-ink)',
          }}>TOURNAMENT · COMPLETE · GUESS THE SPOT</div>
        </div>
        <ThemePicker />
      </div>

      {/* Champion banner */}
      <div style={{ padding: '8px 16px 4px' }}>
        <div style={{
          position: 'relative',
          background: 'var(--c-yellow)',
          border: 'var(--border-ink)',
          borderRadius: 'var(--radius-card)',
          boxShadow: 'var(--shadow-hard)',
          padding: '14px 16px',
          overflow: 'hidden',
          animation: 'penBounceIn 520ms var(--ease-pop) both',
        }}>
          {/* riso-pink offset blob behind */}
          <div style={{
            position: 'absolute', top: -10, right: -10,
            width: 90, height: 90,
            background: 'var(--c-pink)',
            opacity: 0.32,
            borderRadius: '50%',
          }} />
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: 1.2,
            color: 'var(--c-ink)', opacity: 0.85,
            position: 'relative',
          }}>★ CHAMPION ★</div>
          {isDesktop ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginTop: 8, position: 'relative' }}>
              <div style={{
                width: 80, height: 80, borderRadius: 999,
                background: 'var(--c-cream)',
                border: 'var(--border-ink)',
                boxShadow: 'var(--shadow-hard-sm)',
                display: 'grid', placeItems: 'center',
                fontSize: 42, flexShrink: 0,
              }}>{playerStyleFor(indexOfPlayer(players, winner?.id) >= 0 ? indexOfPlayer(players, winner?.id) : 0).emoji}</div>
              <div style={{
                fontFamily: 'var(--font-display)', fontSize: 56, lineHeight: 1,
                color: 'var(--c-ink)',
                textTransform: 'uppercase', letterSpacing: -0.5,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                flex: 1, minWidth: 0,
              }}>{(winner?.id || 'NOBODY').toString().toUpperCase()}</div>
            </div>
          ) : (
            <div style={{
              fontFamily: 'var(--font-display)', fontSize: 30, lineHeight: 1,
              marginTop: 6, color: 'var(--c-ink)',
              textTransform: 'uppercase', letterSpacing: -0.5,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              position: 'relative',
            }}>{(winner?.id || 'NOBODY').toString().toUpperCase()}</div>
          )}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, marginTop: 10,
            position: 'relative',
          }}>
            <div style={{
              fontFamily: 'var(--font-display)', fontSize: 16,
              padding: '6px 10px',
              background: 'var(--c-ink)', color: 'var(--c-yellow)',
              borderRadius: 8, letterSpacing: 0.6, lineHeight: 1,
            }}>+{winner?.score ?? 0} PTS</div>
            {sortedScores.length > 1 && (
              <div style={{
                fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
                color: 'var(--c-ink)', opacity: 0.75, letterSpacing: 0.4,
              }}>OUT OF {sortedScores.length} PLAYERS · {totalRounds} ROUNDS</div>
            )}
            {sortedScores.length === 1 && (
              <div style={{
                fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
                color: 'var(--c-ink)', opacity: 0.75, letterSpacing: 0.4,
              }}>SOLO RUN · {totalRounds} ROUNDS</div>
            )}
          </div>
        </div>
      </div>

      {/* Podium */}
      {podiumOrder.length > 0 && (
        <div style={{ padding: '18px 16px 6px' }}>
          <div style={{
            fontFamily: 'var(--font-display)', fontSize: 11, letterSpacing: 0.8,
            color: 'var(--c-ink)', marginBottom: 10,
          }}>PODIUM</div>
          <div style={{
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            gap: 10, minHeight: 150,
          }}>
            {podiumOrder.map(({ entry, rank }, i) => {
              const playerIdx = indexOfPlayer(players, entry.id);
              const palette = playerStyleFor(playerIdx >= 0 ? playerIdx : rank);
              const isMe = entry.id === currentPlayerId;
              return (
                <div key={entry.id} style={{
                  flex: 1, maxWidth: 110,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                  animation: `penBounceIn 480ms ${300 + i * 140}ms var(--ease-pop) both`,
                }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 999,
                    background: palette.color,
                    border: 'var(--border-ink)',
                    boxShadow: 'var(--shadow-hard-sm)',
                    display: 'grid', placeItems: 'center',
                    fontSize: 22,
                    position: 'relative',
                  }}>
                    {palette.emoji}
                    {isMe && (
                      <div style={{
                        position: 'absolute', bottom: -4, right: -4,
                        width: 18, height: 18, borderRadius: 999,
                        background: 'var(--c-ink)',
                        color: 'var(--c-yellow)',
                        border: '2px solid var(--c-cream)',
                        fontFamily: 'var(--font-display)', fontSize: 10,
                        display: 'grid', placeItems: 'center', lineHeight: 1,
                      }}>★</div>
                    )}
                  </div>
                  <div style={{
                    fontFamily: 'var(--font-display)', fontSize: 11, letterSpacing: 0.4,
                    color: 'var(--c-ink)',
                    maxWidth: '100%',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{(isMe ? 'YOU' : entry.id || '—').toString().toUpperCase()}</div>
                  <div style={{
                    width: '100%',
                    height: PODIUM_HEIGHTS[rank],
                    background: PODIUM_TINTS[rank],
                    border: 'var(--border-ink)',
                    borderRadius: '10px 10px 0 0',
                    boxShadow: 'var(--shadow-hard)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
                    paddingTop: 8, gap: 4,
                  }}>
                    <div style={{
                      fontFamily: 'var(--font-display)', fontSize: 18,
                      color: 'var(--c-ink)',
                    }}>{PODIUM_LABELS[rank]}</div>
                    <div style={{
                      fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700,
                      padding: '2px 6px',
                      background: 'var(--c-ink)', color: 'var(--c-yellow)',
                      borderRadius: 4,
                    }}>+{entry.score ?? 0}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tournament tape — your scorecard */}
      {myRoundScores.length > 0 && (
        <div style={{ padding: '18px 16px 0' }}>
          <div style={{
            display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            marginBottom: 10, gap: 8,
          }}>
            <div style={{
              fontFamily: 'var(--font-display)', fontSize: 11, letterSpacing: 0.8,
              color: 'var(--c-ink)',
            }}>YOUR TAPE</div>
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
              color: 'var(--c-ink)', opacity: 0.7, letterSpacing: 0.4,
            }}>
              {myRank != null ? `RANK #${myRank} · ${myEntry?.score ?? 0} PTS` : `${myEntry?.score ?? 0} PTS`}
            </div>
          </div>

          {/* per-round score chips */}
          <div style={{
            display: 'grid', gridTemplateColumns: `repeat(${myRoundScores.length}, 1fr)`,
            gap: 6,
          }}>
            {myRoundScores.map((rs, i) => {
              const isClosest = closest && rs.round === closest.round;
              const isFurthest = furthest && rs.round === furthest.round && !isClosest;
              const tone = isClosest ? 'var(--c-green)' : isFurthest ? 'var(--c-pink)' : 'var(--c-cream)';
              const accent = isClosest || isFurthest ? 'var(--c-cream)' : 'var(--c-ink)';
              return (
                <div key={rs.round} style={{
                  background: tone,
                  border: 'var(--border-ink-sm)',
                  borderRadius: 6,
                  padding: '6px 4px',
                  textAlign: 'center',
                  animation: `penBounceIn 360ms ${800 + i * 80}ms var(--ease-pop) both`,
                }}>
                  <div style={{
                    fontFamily: 'var(--font-mono)', fontSize: 8, fontWeight: 700,
                    color: accent, opacity: 0.85, letterSpacing: 0.4,
                  }}>R{rs.round}</div>
                  <div style={{
                    fontFamily: 'var(--font-display)', fontSize: 14, lineHeight: 1.05,
                    color: accent, marginTop: 2,
                  }}>+{rs.score}</div>
                  {typeof rs.distance === 'number' && (
                    <div style={{
                      fontFamily: 'var(--font-mono)', fontSize: 7, fontWeight: 700,
                      color: accent, opacity: 0.85, marginTop: 1,
                    }}>{formatDistance(rs.distance)}</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* callouts */}
          {(closest || furthest) && (
            <div style={{
              display: 'flex', flexDirection: 'column', gap: 6,
              marginTop: 10,
            }}>
              {closest && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 10px',
                  background: 'var(--c-cream)',
                  border: 'var(--border-ink-sm)',
                  borderRadius: 6,
                  animation: 'penFadeSlideIn 480ms 1100ms var(--ease-pop) both',
                }}>
                  <div style={{
                    width: 18, height: 18, borderRadius: 4,
                    background: 'var(--c-green)', border: 'var(--border-ink-sm)',
                    color: 'var(--c-cream)',
                    display: 'grid', placeItems: 'center',
                    fontFamily: 'var(--font-display)', fontSize: 11,
                  }}>★</div>
                  <div style={{
                    fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
                    color: 'var(--c-ink)', letterSpacing: 0.4,
                    flex: 1, minWidth: 0,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    CLOSEST CALL · R{closest.round}{closest.cricketer ? ` · ${closest.cricketer.toUpperCase()}` : ''} · {formatDistance(closest.distance)}
                  </div>
                </div>
              )}
              {furthest && furthest.round !== closest?.round && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 10px',
                  background: 'var(--c-cream)',
                  border: 'var(--border-ink-sm)',
                  borderRadius: 6,
                  animation: 'penFadeSlideIn 480ms 1280ms var(--ease-pop) both',
                }}>
                  <div style={{
                    width: 18, height: 18, borderRadius: 4,
                    background: 'var(--c-pink)', border: 'var(--border-ink-sm)',
                    color: 'var(--c-cream)',
                    display: 'grid', placeItems: 'center',
                    fontFamily: 'var(--font-display)', fontSize: 11,
                  }}>✗</div>
                  <div style={{
                    fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
                    color: 'var(--c-ink)', letterSpacing: 0.4,
                    flex: 1, minWidth: 0,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    WORST MISS · R{furthest.round}{furthest.cricketer ? ` · ${furthest.cricketer.toUpperCase()}` : ''} · {formatDistance(furthest.distance)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Also-ran */}
      {rest.length > 0 && (
        <div style={{ padding: '18px 16px 0' }}>
          <div style={{
            fontFamily: 'var(--font-display)', fontSize: 11, letterSpacing: 0.8,
            color: 'var(--c-ink)', marginBottom: 8,
          }}>ALSO RAN</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {rest.map((entry, i) => {
              const rank = i + 4;
              const playerIdx = indexOfPlayer(players, entry.id);
              const palette = playerStyleFor(playerIdx >= 0 ? playerIdx : rank - 1);
              const isMe = entry.id === currentPlayerId;
              return (
                <div key={entry.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 10px',
                  background: isMe ? 'var(--c-yellow)' : 'var(--c-cream)',
                  border: isMe ? 'var(--border-ink)' : 'var(--border-ink-sm)',
                  borderRadius: 'var(--radius-chip)',
                  boxShadow: isMe ? 'var(--shadow-hard-sm)' : 'none',
                  animation: `penBounceIn 360ms ${1300 + i * 80}ms var(--ease-pop) both`,
                }}>
                  <div style={{
                    fontFamily: 'var(--font-display)', fontSize: 13,
                    background: 'var(--c-ink)', color: 'var(--c-cream)',
                    padding: '4px 8px', borderRadius: 6,
                    minWidth: 28, textAlign: 'center',
                  }}>#{rank}</div>
                  <div style={{
                    width: 22, height: 22, borderRadius: 999,
                    background: palette.color, border: 'var(--border-ink-sm)',
                    display: 'grid', placeItems: 'center', fontSize: 11,
                    flexShrink: 0,
                  }}>{palette.emoji}</div>
                  <div style={{
                    flex: 1, fontFamily: 'var(--font-display)', fontSize: 12, letterSpacing: 0.4,
                    color: 'var(--c-ink)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{(isMe ? 'YOU' : entry.id || '—').toString().toUpperCase()}</div>
                  <div style={{
                    fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 13,
                    padding: '3px 7px', borderRadius: 5,
                    background: 'var(--c-ink)', color: 'var(--c-yellow)',
                    minWidth: 36, textAlign: 'right',
                  }}>+{entry.score ?? 0}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* CTA */}
      <div style={{ padding: '20px 16px 8px', display: 'flex', gap: 8 }}>
        <BigBtn onClick={onPlayAgain} primary>
          {isWinner ? '🏆 PLAY AGAIN →' : '🔁 PLAY AGAIN →'}
        </BigBtn>
        <BigBtn onClick={onExitToHome}>🏠 EXIT TO HOME</BigBtn>
      </div>
    </div>
  );
};

export default GuessGameFinishedScreen;
